import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from '@tanstack/react-router'
import { sendEvent } from '@/shared/hooks/use-analytics'
import { authClient } from '@/shared/lib/betterAuth/auth-client'
import {
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_PROFILE_KEY,
  OTP_TYPE_SIGN_IN,
  OTP_EMAIL_KEY,
  OTP_EXPIRED_REGEX,
} from '@/shared/constants/auth'
import { ROUTES } from '@/shared/constants/routes'
import { removeSecureItem } from '@/shared/lib/storage'
import { Route } from './page.verify-code.route'
import type { BetterAuthUser } from '@/shared/types/global.types'
import { createUserSession } from '@/shared/lib/betterAuth/create-user-session'
import { onboardingRedirect } from '@/shared/helpers/onboarding-redirect'

export function useVerifyCodePage() {
  const { email: emailParam, redirect = '' } = Route.useSearch() as {
    email?: string
    redirect?: string
  }
  const email = emailParam || sessionStorage.getItem(OTP_EMAIL_KEY) || ''
  const router = useRouter()
  const { t } = useTranslation()
  const [otp, setOtp] = useState('')
  const [errorKey, setErrorKey] = useState('')
  const [loadingVerify, setLoadingVerify] = useState(false)
  const [loadingResend, setLoadingResend] = useState(false)
  const [otpResetKey, setOtpResetKey] = useState(0)

  async function handleOtpSubmit(otp: string) {
    const currentEmail = sessionStorage.getItem(OTP_EMAIL_KEY) || email
    if (!currentEmail) return
    setErrorKey('')
    setLoadingVerify(true)
    try {
      const result = await authClient.signIn.emailOtp({
        email: currentEmail,
        otp,
      })

      if (result.error) {
        const msg = result.error.message ?? ''
        const expired = OTP_EXPIRED_REGEX.test(msg)
        setErrorKey(expired ? 'login.otp.expired' : 'login.otp.error')
        setOtp('')
        setOtpResetKey((k) => k + 1)
        sendEvent('validate_otp_failure', { email: currentEmail })
        return
      }

      const data = result.data as {
        token: string
        session?: { token: string }
        user?: BetterAuthUser
      }

      if (!data?.token) {
        setErrorKey('login.otp.error')
        setOtp('')
        setOtpResetKey((k) => k + 1)
        sendEvent('validate_otp_failure', { email: currentEmail })
        return
      }

      sendEvent('validate_otp_success', { email: currentEmail })

      sessionStorage.removeItem(OTP_EMAIL_KEY)
      const { identifyEmail } = await createUserSession(data, currentEmail)
      sendEvent('logged_in_with_otp', { email: identifyEmail })

      const redirectUrl = await onboardingRedirect(redirect || ROUTES.HOME)
      router.navigate({ href: redirectUrl })
    } catch {
      setErrorKey(navigator.onLine ? 'login.otp.error' : 'login.offline')
      await removeSecureItem(AUTH_TOKEN_KEY)
      await removeSecureItem(REFRESH_TOKEN_KEY)
      await removeSecureItem(USER_PROFILE_KEY)
    } finally {
      setLoadingVerify(false)
    }
  }

  async function handleResendOtp() {
    const currentEmail = sessionStorage.getItem(OTP_EMAIL_KEY) || email
    if (!currentEmail) return
    setErrorKey('')
    setOtp('')
    setOtpResetKey((k) => k + 1)
    setLoadingResend(true)
    try {
      const { error } = await authClient.emailOtp.sendVerificationOtp({
        email: currentEmail,
        type: OTP_TYPE_SIGN_IN,
      })
      if (error) {
        setErrorKey('login.error')
        sendEvent('request_otp_failure', { email: currentEmail })
      } else {
        sendEvent('request_otp_success', { email: currentEmail })
      }
    } catch {
      setErrorKey('login.offline')
    } finally {
      setLoadingResend(false)
    }
  }

  return {
    email,
    emailParam,
    otp,
    setOtp,
    error: errorKey ? t(errorKey) : '',
    loadingVerify,
    loadingResend,
    otpResetKey,
    handleOtpSubmit,
    handleResendOtp,
    handleBack: () => router.history.back(),
  }
}
